import { useEffect, useRef } from 'react';
import { HashRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import BottomNav from '@/components/BottomNav';
import DesktopLayout from '@/components/DesktopLayout';
import FeedPage from '@/pages/FeedPage';
import AuthPage from '@/pages/AuthPage';
import UploadPage from '@/pages/UploadPage';
import ProfilePage from '@/pages/ProfilePage';
import AdminPage from '@/pages/AdminPage';
import ExplorePage from '@/pages/ExplorePage';
import ScoreboardPage from '@/pages/ScoreboardPage';
import PlayPage from '@/pages/PlayPage';
import MarketplacePage from '@/pages/MarketplacePage';
import CreateListingPage from '@/pages/CreateListingPage';
import ListingDetailPage from '@/pages/ListingDetailPage';
import MessagesPage from '@/pages/MessagesPage';
import '@/App.css';

function AuthCallback() {
  const navigate = useNavigate();
  const { exchangeSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? match[1] : null;

    if (sessionId) {
      // Clear the hash immediately
      window.history.replaceState(null, '', window.location.pathname);
      exchangeSession(sessionId)
        .then(() => navigate('/', { replace: true }))
        .catch(() => navigate('/auth', { replace: true }));
    } else {
      navigate('/auth', { replace: true });
    }
  }, [exchangeSession, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-3">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-muted-foreground font-sans">Signing you in...</p>
      </div>
    </div>
  );
}

function AppLayout() {
  const location = useLocation();
  const { loading } = useAuth();

  // CRITICAL: Use raw browser hash, NOT React Router's location.hash.
  // HashRouter consumes the '#' fragment for routing, so location.hash is empty
  // when Emergent OAuth redirects back with #session_id=XXX. The raw browser
  // hash always contains the OAuth callback fragment regardless of router type.
  if (window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const hideNav = ['/auth'].includes(location.pathname) || location.pathname === '/play';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <h1 className="font-serif text-3xl text-primary font-semibold tracking-tight">GolfScroll</h1>
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className={hideNav ? '' : 'pb-16 lg:pb-0'}>
      <DesktopLayout>
        <Routes>
          <Route path="/" element={<FeedPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/upload" element={<UploadPage />} />
          <Route path="/scorecard" element={<ScoreboardPage />} />
          <Route path="/play" element={<PlayPage />} />
          <Route path="/shop" element={<MarketplacePage />} />
          <Route path="/shop/new" element={<CreateListingPage />} />
          <Route path="/shop/:listingId" element={<ListingDetailPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/profile/:userId" element={<ProfilePage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </DesktopLayout>
      {!hideNav && <BottomNav />}
    </div>
  );
}

function App() {
  return (
    <HashRouter>
      <AuthProvider>
        <AppLayout />
        <Toaster position="top-center" />
      </AuthProvider>
    </HashRouter>
  );
}

export default App;
